import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowLeft } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Card, CardBody } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useCreateTicket } from '@/api/tickets';
import type { CreateTicketRequestPayload } from '@/api/tickets';
import { ticketFormSchema, type TicketFormValues } from './ticket-form-schema';
import { ClassificationSection } from './sections/classification-section';
import { CustomerServiceSection } from './sections/customer-service-section';
import { ContentSection } from './sections/content-section';
import { AssigneesSection } from './sections/assignees-section';
import { PriorityScheduleSection } from './sections/priority-schedule-section';

/**
 * `/tickets/new` — full-page create form for a new support ticket.
 *
 * URL prefill params (cross-agent seam, do not rename):
 *   ?customer_id=<number>  — pre-selects customer picker
 *   ?service_id=<number>   — pre-selects service picker after customer loads
 *
 * On success: navigates to `/tickets/:id`.
 * On error: shows inline Alert with API message.
 */
export default function TicketNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [submitError, setSubmitError] = useState('');

  const createTicket = useCreateTicket();

  const methods = useForm<TicketFormValues>({
    resolver: yupResolver(ticketFormSchema) as never,
    defaultValues: {
      ticket_type_id: 0,
      ticket_type_key: '',
      ticket_type_group: '',
      ticket_priority: 'low',
      ticket_status: 'opening',
      ticket_security_tag: 'none',
      ticket_meeting_type: 'online',
      ticket_meeting_channel: '',
      ticket_meeting_via: '',
      ticket_meeting_purpose: '',
      customer_id: 0,
      service_id: 0,
      ticket_product_name: '',
      ticket_request_id: 0,
      ticket_content: '',
      ticket_affected: '',
      ticket_avoid: '',
      ticket_root_cause: '',
      ticket_remedial_measures: '',
      ticket_internal_root_cause: '',
      ticket_internal_remedial_measures: '',
      ticket_internal_avoid: '',
      ticket_technical_assignees: [],
      ticket_cs_assignees: [],
      ticket_sale_assignees: [],
      ticket_requested_at: null,
      ticket_time_repply: null,
      ticket_estimated_completion_date: null,
      service_startdate: null,
      service_duedate: null,
      service_suspend_until: null,
      ticket_marjorable: false,
      ticket_id: 0,
      employee_id: 0,
    },
  });

  const { handleSubmit, setValue, formState: { isSubmitting } } = methods;

  /* Pre-fill from URL search params */
  useEffect(() => {
    const customerId = Number(searchParams.get('customer_id'));
    if (customerId > 0) setValue('customer_id', customerId);

    const serviceId = Number(searchParams.get('service_id'));
    if (serviceId > 0) setValue('service_id', serviceId);
  }, [searchParams, setValue]);

  const onSubmit = async (data: TicketFormValues) => {
    setSubmitError('');
    try {
      const payload: CreateTicketRequestPayload = {
        note: {
          ticket_type_id: data.ticket_type_id,
          ticket_request_id: data.ticket_request_id || undefined,
          ticket_priority: data.ticket_priority,
          ticket_status: data.ticket_status,
          ticket_security_tag: data.ticket_security_tag,
          ticket_content: data.ticket_content,
          customer_id: data.customer_id,
          service_id: Number(data.service_id) || 0,
          ticket_product_name: data.ticket_product_name || '',
          ticket_technical_assignees: data.ticket_technical_assignees,
          ticket_cs_assignees: data.ticket_cs_assignees,
          ticket_sale_assignees: data.ticket_sale_assignees,
          ticket_requested_at: data.ticket_requested_at ?? undefined,
          ticket_time_repply: data.ticket_time_repply ?? null,
          ticket_estimated_completion_date: data.ticket_estimated_completion_date ?? null,
          service_startdate: data.service_startdate ?? null,
          service_duedate: data.service_duedate ?? null,
          service_suspend_until: data.service_suspend_until ?? null,
          ticket_meeting_type: data.ticket_meeting_type || undefined,
          ticket_meeting_channel: data.ticket_meeting_channel || undefined,
          ticket_meeting_via: data.ticket_meeting_via || undefined,
          ticket_meeting_purpose: data.ticket_meeting_purpose || undefined,
          ticket_affected: data.ticket_affected || '',
          ticket_avoid: data.ticket_avoid || '',
          ticket_root_cause: data.ticket_root_cause || '',
          ticket_remedial_measures: data.ticket_remedial_measures || '',
          ticket_internal_root_cause: data.ticket_internal_root_cause || undefined,
          ticket_internal_remedial_measures: data.ticket_internal_remedial_measures || undefined,
          ticket_internal_avoid: data.ticket_internal_avoid || undefined,
          ticket_marjorable: data.ticket_marjorable ?? false,
          customer_journey_id: data.customer_journey_id,
        },
      };

      const res = await createTicket.mutateAsync(payload);
      const newId = (res?.result as { ticket_id?: number })?.ticket_id;
      navigate(newId ? `/tickets/${newId}` : '/tickets');
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to create ticket. Please try again.',
      );
    }
  };

  const isPending = isSubmitting || createTicket.isPending;

  return (
    <Container size="lg">
      <PageHeader
        title="New Ticket"
        description="Create a new support or service ticket."
        actions={
          <Button
            variant="ghost"
            startIcon={<ArrowLeft size={15} />}
            onClick={() => navigate('/tickets')}
          >
            Back to tickets
          </Button>
        }
      />

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
            <ClassificationSection />
            <CustomerServiceSection />
            <ContentSection />
            <AssigneesSection />
            <PriorityScheduleSection />

            {submitError && (
              <Alert tone="danger" title="Submission error">{submitError}</Alert>
            )}

            {/* Form footer — visible action bar */}
            <Card>
              <CardBody
                style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-3)' }}
              >
                <Button
                  variant="ghost"
                  type="button"
                  disabled={isPending}
                  onClick={() => navigate('/tickets')}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={isPending}>
                  Create ticket
                </Button>
              </CardBody>
            </Card>
          </div>
        </form>
      </FormProvider>
    </Container>
  );
}
